import { AssemblyModalitiesNumber } from 'helpers/enums/Assembleia';
import {
  allIsFalse,
  DEFAULT_VALID,
  DEFAULT_VALIDATION_MODEL,
  firstDateIsBefore,
  requiredIsValid,
  validaData,
  validaHora,
  ValidationModel,
} from 'helpers/utils/ValidationUtils';

import moment from 'moment';
import { useMemo } from 'react';
import { AssembleiaAssessorDadosInformativos } from 'store/modules/AssembleiasAssessorReducer';

export type InfoAssemblyType = {
  [k in keyof IType]?: ValidationModel;
} & {
  allIsValid: boolean;
};

type IType = {
  title: string;
  description: string;
  url: string;
  initialDateOnline: string;
  initialHourOnline: string;
  endDateOnline: string;
  endHourOnline: string;
  initialDatePresential: string;
  initialHourPresential: string;
  endDatePresential: string;
  endHourPresential: string;
};

export default function InfoAssemblyValidator(
  inputs: Partial<IType>,
  active?: Partial<AssembleiaAssessorDadosInformativos>,
  modality?: AssemblyModalitiesNumber
) {
  const validator: Partial<InfoAssemblyType> = {};

  validator.title = useMemo(() => {
    if (active?.nome) {
      const regex = /<[^>]*>/g;
      const value = String(inputs.title).replace(regex, '');
      const required = requiredIsValid(value!, 'Título é obrigatório');

      if (value.length > 200) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Tamanho do título superior a 200 caracteres'
        );
      }

      if (required.error) {
        return required;
      }
    }
    return DEFAULT_VALID;
  }, [inputs.title, active?.nome]);

  validator.url = useMemo(() => {
    if (active?.urlTransmissao) {
      const regex =
        /(https?:\/\/)(www\.)?(youtube\.com\/(watch\?v=|live\/)([a-zA-Z0-9_-]{11})|youtu\.be\/([a-zA-Z0-9_-]{11})|m\.youtube\.com\/(watch\?v=|live\/)([a-zA-Z0-9_-]{11}))|https?:\/\/(www\.)?vimeo\.com\/([0-9]{9})/g;
      const value = inputs.url;
      const required = requiredIsValid(value!, 'O link é obrigatório');

      if (required.error) {
        return required;
      }

      if (!regex.test(value!)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'O link não é do YouTube ou Vimeo'
        );
      }
    }

    return DEFAULT_VALID;
  }, [inputs.url, active?.urlTransmissao]);

  // Date

  validator.initialDateOnline = useMemo(() => {
    if (active?.dataEventoOnline || active?.horarioEventoOnline) {
      const dataInicio = inputs.initialDateOnline;
      const dataFim = inputs.endDateOnline;
      const isValid = validaData(dataInicio!);
      const modo = modality;
      const required = requiredIsValid(
        dataInicio,
        'Data de início é obrigatória'
      );

      const dataFimPresencial = inputs.endDatePresential;
      const horaFimPresencial = inputs.endHourPresential;
      const dataInicioDigital = inputs.initialDateOnline;
      const horaInicioDigital = inputs.initialHourOnline;
      const fimPresencial = moment(
        `${dataFimPresencial} ${horaFimPresencial}`,
        'DD/MM/YYYY HH:mm'
      );
      const inicioDigital = moment(
        `${dataInicioDigital} ${horaInicioDigital}`,
        'DD/MM/YYYY HH:mm'
      );

      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        ![
          AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO,
          AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA,
        ].includes(modo!) &&
        new Date(
          new Date(moment(dataInicio, 'DD/MM/YYYY').toDate()).setUTCHours(
            23,
            59,
            59,
            999
          )
        ) < new Date(new Date().setUTCHours(23, 59, 59, 999))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data início não pode ser inferior a data atual'
        );
      }

      if (firstDateIsBefore(String(dataFim)!, String(dataInicio!))) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de início deve ser anterior à data de término'
        );
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        if (fimPresencial.isAfter(inicioDigital)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de início deve ser posterior à data presencial'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataEventoOnline,
    active?.horarioEventoOnline,
    inputs.endDateOnline,
    inputs.endDatePresential,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    inputs.initialHourOnline,
    modality,
  ]);

  validator.initialDatePresential = useMemo(() => {
    const dataInicio = inputs.initialDatePresential;
    const dataInicioDigital = inputs.initialDateOnline;
    const HoraInicio = inputs.initialHourPresential;
    const horaInicioDigital = inputs.initialHourOnline;
    const dataFim = inputs.endDatePresential;
    const isValid = validaData(dataInicio!);
    const modo = modality;
    const required = requiredIsValid(
      dataInicio,
      'Data de início é obrigatória'
    );

    const initialPresential = moment(
      `${dataInicio} ${HoraInicio}`,
      'DD/MM/YYYY HH:mm'
    );
    const initialOnline = moment(
      `${dataInicioDigital} ${horaInicioDigital}`,
      'DD/MM/YYYY HH:mm'
    );

    if (active?.dataEvento || active?.horarioEvento) {
      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        [
          AssemblyModalitiesNumber.ONLINE,
          AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA,
          AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO,
        ].includes(modo!)
      ) {
        if (
          new Date(
            new Date(moment(dataInicio, 'DD/MM/YYYY').toDate()).setUTCHours(
              23,
              59,
              59,
              999
            )
          ) < new Date(new Date().setUTCHours(23, 59, 59, 999))
        ) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data início não pode ser inferior a data atual'
          );
        }

        if (firstDateIsBefore(dataFim!, dataInicio!)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de início deve ser anterior à data de término'
          );
        }
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        if (firstDateIsBefore(dataFim!, dataInicio!)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de início deve ser anterior à data de término'
          );
        }

        if (initialPresential.isAfter(initialOnline)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de ínicio deve ser anterior à data digital'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataEvento,
    active?.horarioEvento,
    inputs.endDatePresential,
    inputs.initialDateOnline,
    inputs.initialDatePresential,
    inputs.initialHourOnline,
    inputs.initialHourPresential,
    modality,
  ]);

  validator.endDateOnline = useMemo(() => {
    if (active?.dataFimEventoOnline || active?.horarioFimEventoOnline) {
      const dataFim = inputs.endDateOnline;
      const isValid = validaData(dataFim!);
      const modo = modality;
      const required = requiredIsValid(
        dataFim,
        'Data de término é obrigatória'
      );

      const dataFimPresencial = inputs.endDatePresential;
      const horaFimPresencial = inputs.endHourPresential;
      const dataFimDigital = inputs.endDateOnline;
      const horaFimDigital = inputs.endHourOnline;
      const fimPresencial = moment(
        `${dataFimPresencial} ${horaFimPresencial}`,
        'DD/MM/YYYY HH:mm'
      );
      const fimDigital = moment(
        `${dataFimDigital} ${horaFimDigital}`,
        'DD/MM/YYYY HH:mm'
      );

      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        ![
          AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO,
          AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA,
        ].includes(modo!) &&
        new Date(
          new Date(moment(dataFim, 'DD/MM/YYYY').toDate()).setUTCHours(
            23,
            59,
            59,
            999
          )
        ) < new Date(new Date().setUTCHours(23, 59, 59, 999))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de término não pode ser inferior a data atual'
        );
      }

      const dataInicio = inputs.initialDateOnline;
      if (firstDateIsBefore(dataFim!, dataInicio!)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de término deve ser posterior à data de início'
        );
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        if (fimPresencial.isAfter(fimDigital)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de término deve ser posterior à data presencial'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataFimEventoOnline,
    active?.horarioFimEventoOnline,
    inputs.endDateOnline,
    inputs.endDatePresential,
    inputs.endHourOnline,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    modality,
  ]);

  validator.endDatePresential = useMemo(() => {
    const dataFim = inputs.endDatePresential;
    const horaFim = inputs.endHourPresential;
    const dataInicio = inputs.initialDatePresential;
    const dataInicioDigital = inputs.initialDateOnline;
    const horaInicioDigital = inputs.initialHourOnline;
    const required = requiredIsValid(dataFim, 'Data de término é obrigatória');
    const isValid = validaData(dataFim!);
    const modo = modality;

    const endPresential = moment(`${dataFim} ${horaFim}`, 'DD/MM/YYYY HH:mm');
    const initialOnline = moment(
      `${dataInicioDigital} ${horaInicioDigital}`,
      'DD/MM/YYYY HH:mm'
    );

    if (active?.dataFimEvento || active?.horarioFimEvento) {
      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        [
          AssemblyModalitiesNumber.ONLINE,
          AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA,
          AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO,
        ].includes(modo!)
      ) {
        if (
          new Date(
            new Date(moment(dataFim, 'DD/MM/YYYY').toDate()).setUTCHours(
              23,
              59,
              59,
              999
            )
          ) < new Date(new Date().setUTCHours(23, 59, 59, 999))
        ) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de término não pode ser inferior a data atual'
          );
        }

        if (firstDateIsBefore(dataFim!, dataInicio!)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de término deve ser posterior à data de início'
          );
        }
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        if (firstDateIsBefore(dataFim!, dataInicio!)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de término deve ser posterior à data de início'
          );
        }

        if (endPresential.isAfter(initialOnline)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Data de término deve ser anterior à data digital'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataFimEvento,
    active?.horarioFimEvento,
    inputs.endDatePresential,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    inputs.initialDatePresential,
    inputs.initialHourOnline,
    modality,
  ]);

  // Hours

  validator.initialHourOnline = useMemo(() => {
    if (active?.dataEventoOnline || active?.horarioEventoOnline) {
      const initialDate = inputs.initialDateOnline;
      const endDate = inputs.endDateOnline;
      const initialHour = inputs.initialHourOnline;
      const endHour = inputs.endHourOnline;
      const modo = modality!;
      const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
      const fimCurrent = moment(moment(), 'DD/MM/YYYY HH:mm');
      const isValid = validaHora(initialHour!);

      const required = requiredIsValid(
        initialHour,
        'Hora de início é obrigatória'
      );

      const inicio = moment(
        `${initialDate} ${initialHour}`,
        'DD/MM/YYYY HH:mm'
      );

      const dataFimPresencial = inputs.endDatePresential;
      const horaFimPresencial = inputs.endHourPresential;
      const dataInicioDigital = inputs.initialDateOnline;
      const horaInicioDigital = inputs.initialHourOnline;
      const fimPresencial = moment(
        `${dataFimPresencial} ${horaFimPresencial}`,
        'DD/MM/YYYY HH:mm'
      );
      const inicioDigital = moment(
        `${dataInicioDigital} ${horaInicioDigital}`,
        'DD/MM/YYYY HH:mm'
      );

      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        [
          AssemblyModalitiesNumber.ONLINE,
          AssemblyModalitiesNumber.OFFLINE,
        ].includes(modo!) &&
        inicio.isSameOrBefore(fimCurrent)
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Deve ser posterior ao horário atual'
        );
      }

      if (
        initialDate &&
        validaData(initialDate) &&
        endHour &&
        validaHora(endHour) &&
        endDate &&
        validaData(endDate)
      ) {
        if (
          moment(initialDate, 'DD/MM/YYYY').isSame(
            moment(endDate, 'DD/MM/YYYY')
          )
        ) {
          if (inicio.isAfter(fim)) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              'Deve ser anterior à hora de término'
            );
          }
        }
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        const fimCurrent = moment(moment(), 'DD/MM/YYYY HH:mm');

        if (inicioDigital.isSameOrBefore(fimCurrent)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Deve ser posterior ao horário atual'
          );
        }

        if (fimPresencial.isAfter(inicioDigital)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Hora de início deve ser posterior à hora presencial'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataEventoOnline,
    active?.horarioEventoOnline,
    inputs.endDateOnline,
    inputs.endDatePresential,
    inputs.endHourOnline,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    inputs.initialHourOnline,
    modality,
  ]);

  validator.endHourOnline = useMemo(() => {
    if (active?.dataFimEventoOnline || active?.horarioFimEventoOnline) {
      const initialDate = inputs.initialDateOnline;
      const endDate = inputs.endDateOnline;
      const initialHour = inputs.initialHourOnline;
      const endHour = inputs.endHourOnline;
      const isValid = validaHora(endHour!);
      const modo = modality;
      const required = requiredIsValid(
        endHour,
        'Hora de término é obrigatória'
      );

      const dataFimPresencial = inputs.endDatePresential;
      const horaFimPresencial = inputs.endHourPresential;
      const dataFimDigital = inputs.endDateOnline;
      const horaFimDigital = inputs.endHourOnline;
      const fimPresencial = moment(
        `${dataFimPresencial} ${horaFimPresencial}`,
        'DD/MM/YYYY HH:mm'
      );
      const fimDigital = moment(
        `${dataFimDigital} ${horaFimDigital}`,
        'DD/MM/YYYY HH:mm'
      );

      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        initialDate &&
        validaData(initialDate) &&
        initialHour &&
        validaHora(initialHour) &&
        endDate &&
        validaData(endDate)
      ) {
        if (
          moment(initialDate, 'DD/MM/YYYY').isSame(
            moment(endDate, 'DD/MM/YYYY')
          )
        ) {
          const inicio = moment(
            `${initialDate} ${initialHour}`,
            'DD/MM/YYYY HH:mm'
          );
          const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
          if (inicio.isAfter(fim)) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              'Deve ser posterior à hora de início'
            );
          }
        }
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        const fimCurrent = moment(moment(), 'DD/MM/YYYY HH:mm');

        if (fimDigital.isSameOrBefore(fimCurrent)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Deve ser posterior ao horário atual'
          );
        }

        if (fimPresencial.isAfter(fimDigital)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Hora de término deve ser posterior à hora presencial'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataFimEventoOnline,
    active?.horarioFimEventoOnline,
    inputs.endDateOnline,
    inputs.endDatePresential,
    inputs.endHourOnline,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    inputs.initialHourOnline,
    modality,
  ]);

  validator.initialHourPresential = useMemo(() => {
    if (active?.horarioEvento || active?.dataEvento) {
      const initialDate = inputs.initialDatePresential;
      const initialHour = inputs.initialHourPresential;
      const initialDateDigital = inputs.initialDateOnline;
      const initialHourDigital = inputs.initialHourOnline;
      const endDate = inputs.endDatePresential;
      const endHour = inputs.endHourPresential;
      const isValid = validaHora(initialHour!);
      const modo = modality;

      const initialPresential = moment(
        `${initialDate} ${initialHour}`,
        'DD/MM/YYYY HH:mm'
      );
      const initialOnline = moment(
        `${initialDateDigital} ${initialHourDigital}`,
        'DD/MM/YYYY HH:mm'
      );
      const required = requiredIsValid(
        initialHour,
        'Hora de início é obrigatória'
      );

      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        [
          AssemblyModalitiesNumber.ONLINE,
          AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA,
          AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO,
        ].includes(modo!)
      ) {
        const inicio = moment(
          `${initialDate} ${initialHour}`,
          'DD/MM/YYYY HH:mm'
        );

        const fimCurrent = moment(moment(), 'DD/MM/YYYY HH:mm');

        if (inicio.isSameOrBefore(fimCurrent)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Deve ser posterior ao horário atual'
          );
        }

        if (
          initialDate &&
          validaData(initialDate) &&
          endHour &&
          validaHora(endHour) &&
          endDate &&
          validaData(endDate)
        ) {
          if (
            moment(initialDate, 'DD/MM/YYYY').isSame(
              moment(endDate, 'DD/MM/YYYY')
            )
          ) {
            const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');

            if (inicio.isAfter(fim)) {
              return DEFAULT_VALIDATION_MODEL(
                true,
                'Deve ser anterior à hora de término'
              );
            }
          }
        }
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        if (`${initialHour}` >= `${endHour}`) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Horá de início deve ser anterior à hora de término'
          );
        }

        if (initialPresential.isAfter(initialOnline)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Hora de início deve ser anterior à hora digital'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataEvento,
    active?.horarioEvento,
    inputs.endDatePresential,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    inputs.initialDatePresential,
    inputs.initialHourOnline,
    inputs.initialHourPresential,
    modality,
  ]);

  validator.endHourPresential = useMemo(() => {
    if (active?.horarioFimEvento || active?.dataFimEvento) {
      const initialDate = inputs.initialDatePresential;
      const initialDateOnline = inputs.initialDateOnline;
      const endDate = inputs.endDatePresential;
      const initialHour = inputs.initialHourPresential;
      const initialHourOnline = inputs.initialHourOnline;
      const endHour = inputs.endHourPresential;
      const isValid = validaHora(endHour!);
      const modo = modality;
      const required = requiredIsValid(
        endHour,
        'Hora de término é obrigatória'
      );

      const endPresential = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
      const initialOnline = moment(
        `${initialDateOnline} ${initialHourOnline}`,
        'DD/MM/YYYY HH:mm'
      );

      if (!isValid && !required.error) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }

      if (required.error) {
        return required;
      }

      if (
        [
          AssemblyModalitiesNumber.ONLINE,
          AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA,
          AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO,
        ].includes(modo!)
      ) {
        if (
          initialDate &&
          validaData(initialDate) &&
          initialHour &&
          validaHora(initialHour) &&
          endDate &&
          validaData(endDate)
        ) {
          if (
            moment(initialDate, 'DD/MM/YYYY').isSame(
              moment(endDate, 'DD/MM/YYYY')
            )
          ) {
            const inicio = moment(
              `${initialDate} ${initialHour}`,
              'DD/MM/YYYY HH:mm'
            );
            const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
            if (inicio.isAfter(fim)) {
              return DEFAULT_VALIDATION_MODEL(
                true,
                'Deve ser posterior à hora de início'
              );
            }
          }
        }
      }

      if (
        [AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        if (`${endHour}` <= `${initialHour}`) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Horá de término deve ser posterior à hora de início'
          );
        }

        if (endPresential.isAfter(initialOnline)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Hora de término deve ser anterior à hora digital'
          );
        }
      }
    }

    return DEFAULT_VALID;
  }, [
    active?.dataFimEvento,
    active?.horarioFimEvento,
    inputs.endDatePresential,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    inputs.initialDatePresential,
    inputs.initialHourOnline,
    inputs.initialHourPresential,
    modality,
  ]);

  validator.allIsValid = allIsFalse([
    active?.nome ? validator.title.error : false,
    active?.urlTransmissao ? validator.url.error : false,
    active?.dataEventoOnline || active?.horarioEventoOnline
      ? validator.initialDateOnline.error
      : false,
    active?.dataEvento || active?.horarioEvento
      ? validator.initialDatePresential.error
      : false,
    active?.dataFimEventoOnline || active?.horarioFimEvento
      ? validator.endDateOnline.error
      : false,
    active?.dataFimEvento || active?.horarioEvento
      ? validator.endDatePresential.error
      : false,
    active?.horarioEventoOnline || active?.dataEventoOnline
      ? validator.initialHourOnline.error
      : false,
    active?.horarioFimEventoOnline || active?.dataFimEventoOnline
      ? validator.endHourOnline.error
      : false,
    active?.horarioEvento || active?.dataEvento
      ? validator.initialHourPresential.error
      : false,
    active?.horarioFimEvento || active?.dataFimEvento
      ? validator.endHourPresential.error
      : false,
  ]);

  return validator as InfoAssemblyType;
}
